// filter-condition-bar.component.ts
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { ButtonType, ButtonVariant, DropdownOption, DropdownOptionDefault, DropdownOptionGroup, DropdownOptionIcon, DropdownOptionType, DropdownSelectionType, IconName, TextCase, TextCasePipe } from '@yupma/ui-kit';
import { ActivityField, CheckboxOperator, DateOperator, EntityAddFormField, FieldDocument, FieldGroupDocument, FieldOption, FieldType, FilterCondition, FilterOperator, NumberOperator, SelectionOperator, TextOperator, UserDocument } from 'server-model';
import { FieldTypeIconMap } from 'src/app/utils';

@Component({
  selector: 'app-filter-condition-bar',
  templateUrl: './filter-condition-bar.component.html',
  styleUrl: './filter-condition-bar.component.scss',
  providers: [TextCasePipe]
})
export class FilterConditionBarComponent implements OnChanges, OnInit, AfterViewInit {
  @Input() fieldDocuments: FieldDocument[] | ActivityField[] = [];
  @Input() fieldGroups: FieldGroupDocument[] = [];
  @Input() allUsers: UserDocument[] = [];
  @Input() filterCondition: FilterCondition | null = null;
  @Input() conditionId: number = 0;

  @Output() conditionUpdated = new EventEmitter<FilterCondition | null>();
  @Output() onRemove = new EventEmitter<void>();
  @Output() onInvalidationChange = new EventEmitter<boolean>();

  // UI constants
  buttonVariants = ButtonVariant;
  buttonTypes = ButtonType;
  iconNames = IconName;
  dropdownSelectionTypes = DropdownSelectionType;
  textCases = TextCase;
  fieldTypes = FieldType;

  // Component state
  selectedField: FieldDocument | ActivityField | null = null;
  selectedOperator: FilterOperator | null = null;
  currentValue: any = '';

  // Dropdown options
  fieldsDropdownOptions: DropdownOptionGroup[] = [];
  operatorDropdownOptions: DropdownOptionGroup[] = [];

  // Dropdown visibility
  showFieldDropdown = false;
  showOperatorDropdown = false;

  // Cached value for shouldShowValueInput to prevent expression changes
  private _shouldShowValueInput = false;

  // Operators that don't require value input
  private readonly NO_VALUE_OPERATORS: FilterOperator[] = [
    TextOperator.IS_EMPTY,
    TextOperator.IS_NOT_EMPTY,
    NumberOperator.IS_EMPTY,
    NumberOperator.IS_NOT_EMPTY,
    DateOperator.IS_EMPTY,
    DateOperator.IS_NOT_EMPTY,
    CheckboxOperator.IS_EMPTY,
    CheckboxOperator.IS_NOT_EMPTY,
    SelectionOperator.IS_EMPTY,
    SelectionOperator.IS_NOT_EMPTY
  ];

  // Field type to operators mapping
  private readonly FIELD_TYPE_OPERATORS: Record<FieldType, FilterOperator[]> = {
    [FieldType.SINGLE_LINE_TEXT]: Object.values(TextOperator),
    [FieldType.MULTI_LINE_TEXT]: Object.values(TextOperator),
    [FieldType.RICH_TEXT]: Object.values(TextOperator),
    [FieldType.EMAIL]: Object.values(TextOperator),
    [FieldType.PHONE]: Object.values(TextOperator),
    [FieldType.NUMBER]: Object.values(NumberOperator),
    [FieldType.DATE]: Object.values(DateOperator),
    [FieldType.DATE_AND_TIME]: Object.values(DateOperator),
    [FieldType.CHECKBOX]: Object.values(CheckboxOperator),
    [FieldType.DROPDOWN]: Object.values(SelectionOperator),
    [FieldType.MULTI_SELECT_DROPDOWN]: Object.values(SelectionOperator),
    [FieldType.USER]: Object.values(SelectionOperator),
    [FieldType.FILE]: [],
    [FieldType.JSON]: [],
    [FieldType.LOCATION]: []
  };

  // Default operators for each field type
  private readonly DEFAULT_OPERATORS: Record<FieldType, FilterOperator> = {
    [FieldType.SINGLE_LINE_TEXT]: TextOperator.EQUALS,
    [FieldType.MULTI_LINE_TEXT]: TextOperator.EQUALS,
    [FieldType.RICH_TEXT]: TextOperator.EQUALS,
    [FieldType.EMAIL]: TextOperator.EQUALS,
    [FieldType.PHONE]: TextOperator.EQUALS,
    [FieldType.NUMBER]: NumberOperator.EQUALS,
    [FieldType.DATE]: DateOperator.EQUALS,
    [FieldType.DATE_AND_TIME]: DateOperator.EQUALS,
    [FieldType.CHECKBOX]: CheckboxOperator.EQUALS,
    [FieldType.DROPDOWN]: SelectionOperator.IS_ANY,
    [FieldType.MULTI_SELECT_DROPDOWN]: SelectionOperator.IS_ANY,
    [FieldType.USER]: SelectionOperator.IS_ANY,
    [FieldType.FILE]: TextOperator.EQUALS,
    [FieldType.JSON]: TextOperator.EQUALS,
    [FieldType.LOCATION]: TextOperator.EQUALS
  };

  constructor(
    private textCase: TextCasePipe,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngAfterViewInit(): void {
    // Ensure initial condition is emitted after view initialization
    this.cdr.detectChanges();
    this.emitConditionUpdate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fieldDocuments'] || changes['filterCondition']) {
      this.initializeComponent();
    }
  }

  private initializeComponent(): void {
    this.buildFieldsDropdown();

    if (this.filterCondition) {
      this.loadFromCondition(this.filterCondition);
    } else {
      this.selectDefaultField();
    }

    this.updateShouldShowValueInput();
  }

  private loadFromCondition(condition: FilterCondition): void {
    // Find and set the selected field
    this.selectedField = this.fieldDocuments.find(f => f.name === condition.field_name) || null;

    if (this.selectedField) {
      this.buildOperatorDropdown(this.selectedField.field_type as FieldType);

      // Set operator if it's valid for the field type
      const availableOperators = this.FIELD_TYPE_OPERATORS[this.selectedField.field_type as FieldType] || [];
      if (availableOperators.includes(condition.operator)) {
        this.selectedOperator = condition.operator;
      } else {
        // If operator is not valid for field type, use default
        this.selectedOperator = this.getDefaultOperator(this.selectedField.field_type as FieldType);
      }

      this.currentValue = condition.value || '';
    }

    this.updateShouldShowValueInput();
  }

  private selectDefaultField(): void {
    if (this.fieldDocuments.length > 0) {
      this.selectedField = this.fieldDocuments[0];
      this.buildOperatorDropdown(this.selectedField.field_type as FieldType);
      this.selectedOperator = this.getDefaultOperator(this.selectedField.field_type as FieldType);
      this.currentValue = '';
    }

    this.updateShouldShowValueInput();
  }

  private buildFieldsDropdown(): void {
    const fieldOptions = this.fieldDocuments
      .map(field => {
        const fieldType = field.field_type as FieldType || FieldType.SINGLE_LINE_TEXT;
        const icon = FieldTypeIconMap[fieldType];

        return new DropdownOptionIcon(
          DropdownOptionType.DEFAULT,
          field.label,
          field.name,
          icon,
          this.selectedField?.name === field.name
        );
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    this.fieldsDropdownOptions = [new DropdownOptionGroup('', fieldOptions)];
  }

  private buildOperatorDropdown(fieldType: FieldType): void {
    const operators = this.FIELD_TYPE_OPERATORS[fieldType] || Object.values(TextOperator);

    const operatorOptions = operators.map(operator =>
      new DropdownOptionDefault(
        DropdownOptionType.DEFAULT,
        this.textCase.transform(operator),
        operator,
        this.selectedOperator === operator
      )
    );

    this.operatorDropdownOptions = [new DropdownOptionGroup('', operatorOptions)];
  }

  private getDefaultOperator(fieldType: FieldType): FilterOperator {
    return this.DEFAULT_OPERATORS[fieldType] || TextOperator.EQUALS;
  }

  private isNoValueOperator(operator: FilterOperator | null): boolean {
    return operator ? this.NO_VALUE_OPERATORS.includes(operator) : false;
  }

  private updateShouldShowValueInput(): void {
    this._shouldShowValueInput = !!(this.selectedField &&
      this.selectedOperator &&
      !this.isNoValueOperator(this.selectedOperator));
  }

  private emitConditionUpdate(): void {
    if (!this.selectedField || !this.selectedOperator) {
      this.conditionUpdated.emit(null);
      this.onInvalidationChange.emit(true);
      return;
    }

    // For no-value operators, don't require a value
    const finalValue = this.isNoValueOperator(this.selectedOperator) ? '' : this.currentValue;

    // Validate that value is provided when required
    if (!this.isNoValueOperator(this.selectedOperator) && !finalValue && finalValue !== 0) {
      this.conditionUpdated.emit(null);
      this.onInvalidationChange.emit(true);
      return;
    }

    const condition = new FilterCondition(
      this.selectedField.name,
      this.selectedOperator,
      finalValue,
      undefined,
      this.selectedField.field_type as FieldType
    );

    this.conditionUpdated.emit(condition);
    this.onInvalidationChange.emit(false);
  }

  // Event handlers
  toggleFieldDropdown(state: boolean): void {
    this.showFieldDropdown = state;
  }

  toggleOperatorDropdown(state: boolean): void {
    this.showOperatorDropdown = state;
  }

  updateSelectedField(selections: EntityAddFormField[]): void {
    if (selections.length === 0) {
      this.selectedField = null;
      this.selectedOperator = null;
      this.currentValue = '';
      this.updateShouldShowValueInput();
      this.cdr.detectChanges();
      this.emitConditionUpdate();
      return;
    }

    const fieldName = selections[0].field_name;
    const newField = this.fieldDocuments.find(field => field.name === fieldName);

    if (!newField || newField === this.selectedField) {
      return;
    }

    this.selectedField = newField;
    this.buildOperatorDropdown(newField.field_type as FieldType);

    // Reset operator to default for new field type
    this.selectedOperator = this.getDefaultOperator(newField.field_type as FieldType);

    // Reset value when field changes
    this.currentValue = '';

    // Rebuild fields dropdown to update selection
    this.buildFieldsDropdown();

    this.updateShouldShowValueInput();
    this.cdr.detectChanges();
    this.emitConditionUpdate();
  }

  updateOperator(option: DropdownOption): void {
    const newOperator = option.value as FilterOperator;

    // Only proceed if operator actually changed
    if (newOperator === this.selectedOperator) {
      return;
    }

    // Update selection state
    this.operatorDropdownOptions[0].options.forEach(opt => {
      opt.selected = opt === option;
    });

    this.selectedOperator = newOperator;

    // Clear value if switching to a no-value operator
    if (this.isNoValueOperator(this.selectedOperator)) {
      this.currentValue = '';
    }

    this.updateShouldShowValueInput();
    this.cdr.detectChanges();
    this.emitConditionUpdate();
  }

  updateValue(value: any): void {
    this.currentValue = value;
    this.emitConditionUpdate();
  }

  removeCondition(): void {
    this.onRemove.emit();
  }

  // Template helper methods - use cached value to prevent expression changes
  shouldShowValueInput(): boolean {
    return this._shouldShowValueInput;
  }

  getCurrentCondition(): FilterCondition | null {
    if (!this.selectedField || !this.selectedOperator) {
      return null;
    }

    const finalValue = this.isNoValueOperator(this.selectedOperator) ? '' : this.currentValue;

    return new FilterCondition(
      this.selectedField.name,
      this.selectedOperator,
      finalValue,
      undefined,
      this.selectedField.field_type as FieldType
    );
  }
}